import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ThemeStateService } from '../../state/theme-state.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public darkMode$!: Observable<boolean>;

  // Check if we're in a browser environment
  private isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  constructor(private themeState: ThemeStateService) {
    this.darkMode$ = this.themeState.selectDarkMode();

    // Only initialize theme if in browser environment
    if (this.isBrowser()) {
      // Check if user previously selected dark mode
      this.initTheme();
    }
  }

  private initTheme(): void {
    // Check if user has previously set a preference
    const savedTheme = this.getLocalStorage('theme');

    // If user has explicitly chosen a theme, use it
    if (savedTheme) {
      this.setDarkMode(savedTheme === 'dark');
      return;
    }

    // Otherwise, check browser/OS preference
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      this.setDarkMode(true);
    }

    // Set up listener for changes in system preference
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', e => {
        if (!this.getLocalStorage('theme')) {
          // Only auto-switch if user hasn't set preference
          this.setDarkMode(e.matches);
        }
      });
  }

  // Helper method to safely get from localStorage
  private getLocalStorage(key: string): string | null {
    if (this.isBrowser()) {
      return localStorage.getItem(key);
    }
    return null;
  }

  // Helper method to safely set to localStorage
  private setLocalStorage(key: string, value: string): void {
    if (this.isBrowser()) {
      localStorage.setItem(key, value);
    }
  }

  toggleDarkMode(): void {
    this.themeState.toggle();
    this.applyTheme(this.themeState.getState().darkMode);
  }

  setDarkMode(isDark: boolean): void {
    this.themeState.setDarkMode(isDark);
    this.applyTheme(isDark);
  }

  private applyTheme(isDark: boolean): void {
    // Only interact with browser APIs if in browser environment
    if (this.isBrowser()) {
      // Save preference to localStorage
      this.setLocalStorage('theme', isDark ? 'dark' : 'light');

      // Apply theme to document
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }
}
